<template>
  <Card pt:content:class="p-0!" pt:body:class="p-2!">
    <template #header>
      <div class="flex flex-row px-5 py-3 gap-3 justify-center rounded-t-xl bg-primary-200 dark:bg-primary-700">
        <h1 class="font-bold text-xl">Source of Funds</h1>
        <IconButton v-show="!editing && canEdit" v-tooltip.top="'Edit'" rounded icon="pencil" small @click="startEditingSources"> </IconButton>
      </div>
    </template>
    <template #content>
      <SourceOfFundsTable
        ref="sourceTable"
        v-model:project-settings="project.projectSettings"
        v-model:project-entities="sourceOfFunds"
        v-model:drawItems="drawItems"
        :entities
        :draw-number="project.projectDrawInfo.drawNumber"
        :editing
        :draw-locked="project.projectDrawInfo.locked"
        :project-info="project.projectInfo"
        :contacts
      ></SourceOfFundsTable>
      <SubmitOptions v-show="editing" class="mt-3" @cancelled="stopEditingSources" @submitted="submitSourceChanges"></SubmitOptions>
    </template>
  </Card>
</template>

<script setup lang="ts">
import type { ProjectEntity } from "@/types/ProjectEntity";
import { type Project } from "@/types/Projects";
import type { Entity } from "@/types/Entity";
import type { DrawItem } from "@/types/DrawItem";
import type { UserPermissions } from "@/types/User";
import { z } from "zod";

const project = defineModel<Project>("project", { required: true });
const drawItems = defineModel<DrawItem[]>("drawItems", { required: true });
const sourceOfFunds = defineModel<ProjectEntity[]>("sourceOfFunds", { required: true });
const editing = defineModel<boolean>("editing", { required: true });
const editingTitle = defineModel<string>("editingTitle", { required: true });

const emit = defineEmits<{
  startEditing: [];
  sourcesUpdated: [];
}>();

const sourceOfFundsSubmitted = ref(false);
const sourceTable = ref();

let sourceOfFundsHistory = useManualRefHistory(sourceOfFunds, { clone: true });
let projectHistory = useManualRefHistory(project, { clone: true });

interface Props {
  isCurrentDraw: boolean;
  permissions: UserPermissions;
  canEdit: boolean;
  entities: Entity[];
  contacts: ProjectEntity[];
}
const props = defineProps<Props>();

const startEditingSources = (): void => {
  emit("startEditing");
  sourceOfFundsHistory = useManualRefHistory(sourceOfFunds, { clone: true });
  projectHistory = useManualRefHistory(project, { clone: true });
  sourceOfFundsHistory.commit();
  projectHistory.commit();
  editing.value = true;
  editingTitle.value = "Editing Source of Funds";
  setTimeout(() => sourceTable.value.toggleEditing(true), 10);
};

const submitSourceChanges = (): void => {
  let totalBorrowerPercent = 100;
  let totalLenderPercent = 100;
  if (project.value.projectSettings.enableEquityParticipation && project.value.projectSettings.equityParticipationMethod === "PERCENT") {
    const borrowers = sourceOfFunds.value.filter((source) => source.type.toUpperCase().includes("BORROWER"));
    totalBorrowerPercent = borrowers.reduce((accumulator, entity) => accumulator + entity.participationPercent, 0);
  }
  if (project.value.projectSettings.enableLenderParticipation && project.value.projectSettings.participationMethod === "PERCENT") {
    const lenders = sourceOfFunds.value.filter((source) => source.type.toUpperCase().includes("LENDER"));
    totalLenderPercent = lenders.reduce((accumulator, entity) => accumulator + entity.participationPercent, 0);
  }
  if (totalBorrowerPercent !== 100) {
    error("Error", "Borrower splits are not totaling 100%");
    return;
  } else if (totalLenderPercent !== 100) {
    error("Error", "Lender splits are not totaling 100%");
    return;
  }

  sourceOfFundsHistory.commit();
  projectHistory.commit();
  project.value.projectSettings.sourcesColumnOrder = sourceTable.value.getSourceColumnOrder();
  sourceTable.value.rearrangeColumns();
  sourceOfFundsSubmitted.value = true;
  axios
    .post("/draw/update/sources/" + project.value.projectInfo.projectId + "/" + project.value.projectDrawInfo.drawNumber, {
      projectSettings: project.value.projectSettings,
      items: drawItems.value,
      sourceOfFunds: sourceOfFunds.value,
    })
    .then(() => emit("sourcesUpdated"))
    .catch((e) => errorApi(e));
  stopEditingSources();
};

const stopEditingSources = (): void => {
  if (!sourceOfFundsSubmitted.value) {
    sourceOfFundsHistory.undo();
    // projectHistory.undo();
    const lastProjectSnapshot = projectHistory.history.value[0]?.snapshot;

    if (lastProjectSnapshot) {
      const revertedProject = JSON.parse(JSON.stringify(lastProjectSnapshot));

      // Coerce dates immediately before assigning to prevent Zod validation errors
      revertedProject.projectDrawInfo.dateCreated = z.coerce.date().parse(revertedProject.projectDrawInfo.dateCreated);
      revertedProject.projectDrawInfo.completionDate = z.coerce.date().nullable().parse(revertedProject.projectDrawInfo.completionDate);
      revertedProject.projectDrawInfo.inspectionDate = z.coerce.date().nullable().parse(revertedProject.projectDrawInfo.inspectionDate);

      project.value = revertedProject;
    }
  }
  sourceOfFundsSubmitted.value = false;
  editing.value = false;
  editingTitle.value = "";
  setTimeout(() => sourceTable.value.toggleEditing(true), 10);
};

onBeforeMount(async (): Promise<void> => {});
</script>
